import { supabase } from './supabaseService';
import type {
  MaintenanceContract,
  MaintenanceTask,
  MaintenanceReport,
} from '../types/maintenance';

class MaintenanceService {
  // ─── CONTRATOS ──────────────────────────────────────────────

  async getContracts(): Promise<MaintenanceContract[]> {
    try {
      const { data, error } = await supabase
        .from('maintenance_contracts')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching maintenance contracts:', err);
      throw err;
    }
  }

  async getContractById(id: string): Promise<MaintenanceContract | null> {
    try {
      const { data, error } = await supabase
        .from('maintenance_contracts')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching contract by id:', err);
      throw err;
    }
  }

  async getContractByToken(token: string): Promise<MaintenanceContract | null> {
    try {
      const { data, error } = await supabase
        .from('maintenance_contracts')
        .select('*')
        .eq('portal_token', token)
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching contract by token:', err);
      throw err;
    }
  }

  async createContract(
    data: Omit<MaintenanceContract, 'id' | 'portal_token' | 'created_at' | 'updated_at'>
  ): Promise<MaintenanceContract> {
    try {
      const { data: created, error } = await supabase
        .from('maintenance_contracts')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return created;
    } catch (err) {
      console.error('Error creating contract:', err);
      throw err;
    }
  }

  async updateContract(
    id: string,
    data: Partial<MaintenanceContract>
  ): Promise<MaintenanceContract> {
    try {
      const { data: updated, error } = await supabase
        .from('maintenance_contracts')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    } catch (err) {
      console.error('Error updating contract:', err);
      throw err;
    }
  }

  // ─── TAREAS ─────────────────────────────────────────────────

  async getTasksByContract(contractId: string): Promise<MaintenanceTask[]> {
    try {
      const { data, error } = await supabase
        .from('maintenance_tasks')
        .select('*')
        .eq('contract_id', contractId)
        .order('task_date', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching tasks:', err);
      throw err;
    }
  }

  async getTasksByContractAndMonth(
    contractId: string,
    month: string // 'YYYY-MM'
  ): Promise<MaintenanceTask[]> {
    try {
      const contract = await this.getContractById(contractId);
      if (contract?.project_id) {
        const { data, error } = await supabase.rpc('get_project_maintenance', { p_project: contract.project_id, p_month: `${month}-01` });
        if (error) throw error;
        return (data?.lines || []).filter((line: any) => line.work_category === 'maintenance' && line.status === 'approved')
          .map((line: any) => ({ ...line, contract_id: contractId }));
      }
      const startDate = `${month}-01`;
      const [year, mon] = month.split('-').map(Number);
      const endDate = new Date(Date.UTC(year, mon, 1)).toISOString().split('T')[0]; // exclusive next month

      const { data, error } = await supabase
        .from('maintenance_tasks')
        .select('*')
        .eq('contract_id', contractId)
        .gte('task_date', startDate)
        .lt('task_date', endDate)
        .order('task_date', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching tasks by month:', err);
      throw err;
    }
  }

  async createTask(
    data: Omit<MaintenanceTask, 'id' | 'created_at' | 'updated_at'>
  ): Promise<MaintenanceTask> {
    try {
      const { data: created, error } = await supabase
        .from('maintenance_tasks')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return created;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  }

  async updateTask(
    id: string,
    data: Partial<MaintenanceTask>
  ): Promise<MaintenanceTask> {
    try {
      const { data: updated, error } = await supabase
        .from('maintenance_tasks')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }

  async deleteTask(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('maintenance_tasks')
        .delete()
        .eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }

  // ─── HORAS DEL MES ─────────────────────────────────────────

  async getMonthlyHours(
    contractId: string,
    month: string // 'YYYY-MM'
  ): Promise<{
    total: number;
    support: number;
    improvement: number;
    remaining: number;
    extra: number;
  }> {
    try {
      const tasks = await this.getTasksByContractAndMonth(contractId, month);
      const contract = await this.getContractById(contractId);
      if (!contract) throw new Error('Contract not found');

      const support = tasks
        .filter((t) => ['bug_fix', 'support', 'security', 'server'].includes(t.task_type))
        .reduce((sum, t) => sum + Number(t.hours), 0);

      const improvement = tasks
        .filter((t) => t.task_type === 'improvement')
        .reduce((sum, t) => sum + Number(t.hours), 0);

      const total = support + improvement;
      const remaining = Math.max(0, contract.monthly_hours - total);
      const extra = Math.max(0, total - contract.monthly_hours);

      return { total, support, improvement, remaining, extra };
    } catch (err) {
      console.error('Error calculating monthly hours:', err);
      throw err;
    }
  }

  // ─── INFORMES ───────────────────────────────────────────────

  async getReportsByContract(contractId: string): Promise<MaintenanceReport[]> {
    try {
      const { data, error } = await supabase
        .from('maintenance_reports')
        .select('*')
        .eq('contract_id', contractId)
        .order('report_month', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching reports:', err);
      throw err;
    }
  }

  async getOrCreateMonthlyReport(
    contractId: string,
    month: string // 'YYYY-MM'
  ): Promise<MaintenanceReport> {
    const { data, error } = await supabase.rpc('prepare_maintenance_report', {
      p_contract: contractId, p_month: `${month}-01`, p_finalize: false,
    });
    if (error) throw error;
    return data;
  }

  async finalizeReport(contractId: string, month: string): Promise<MaintenanceReport> {
    const { data, error } = await supabase.rpc('prepare_maintenance_report', {
      p_contract: contractId, p_month: `${month}-01`, p_finalize: true,
    });
    if (error) throw error;
    return data;
  }

  async updateReport(
    id: string,
    data: Partial<MaintenanceReport>
  ): Promise<MaintenanceReport> {
    try {
      const { data: updated, error } = await supabase
        .from('maintenance_reports')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    } catch (err) {
      console.error('Error updating report:', err);
      throw err;
    }
  }

  async markReportAsSent(id: string): Promise<MaintenanceReport> {
    try {
      const { data: updated, error } = await supabase
        .from('maintenance_reports')
        .update({ status: 'sent', sent_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return updated;
    } catch (err) {
      console.error('Error marking report as sent:', err);
      throw err;
    }
  }
}

export const maintenanceService = new MaintenanceService();
